/**
 * Shares one `usePendingInvitationsState` between the shell-level
 * `WorkspaceInvitationGate` (the modal), `WorkspaceSwitcher` (Pending rows)
 * and `UserMenu` (opening with the "Welcome aboard" callout after an
 * accept).  Mounted once in `HorizontalNavBar`, so it only exists while the
 * signed-in shell is up; components rendered without it (tests, pages
 * outside the shell) get an inert value with no invitations.
 */
import React, { createContext, useContext } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { usePendingInvitationsState } from '../hooks/usePendingInvitationsState';

const noop = () => {};

export const INERT_PENDING_INVITATIONS = Object.freeze({
    invitations: [],
    dismissed: [],
    current: null,
    position: 0,
    total: 0,
    busy: false,
    busyAction: null,
    accept: noop,
    decline: noop,
    dismiss: noop,
    openFor: noop,
    refresh: noop,
    acceptedIds: [],
    clearAccepted: noop,
    menuOpenRequest: 0,
});

const PendingInvitationsContext = createContext(INERT_PENDING_INVITATIONS);

export function PendingInvitationsProvider({ children, enabled: enabledProp }) {
    const { isAuthEnabled, user, workspace, applyWorkspaceJoined } = useAuth();
    // Only a signed-in person who already has an active workspace can be
    // invited to another one; with auth off there are no invitations at all.
    const enabled = enabledProp ?? Boolean(isAuthEnabled && user && workspace);
    const value = usePendingInvitationsState({ enabled, applyWorkspaceJoined });
    return <PendingInvitationsContext.Provider value={value}>{children}</PendingInvitationsContext.Provider>;
}

export const usePendingInvitations = () => useContext(PendingInvitationsContext);
